//#title="Emergency Calling (E911)"
//#description="User can manage or configure E911 (Enhanced 911) settings for their phone system from this page."

import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { DeviceInclude_Enum, DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { DevicesApi, DevicesQueryKey } from '@frontend/api-devices';
import { E911Api, E911QueryKeys } from '@frontend/api-e911-addresses';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useSettingsNavigate, useSettingsSearch } from '@frontend/settings-routing';
import {
  BannerNotification,
  Modal,
  ModalControlModalProps,
  Button,
  Chip,
  DropdownFilterChipField,
  Table,
  Tabs,
  Text,
  TextLink,
  Truncated,
  useFormField,
  useModalControl,
  useTooltip,
  InlineNotification,
} from '@frontend/design-system';
import { AssignE911AddressModal } from '../../components/assign-e911-address-modal';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { formatE911AddressForDisplay } from '../../utils/e911-address';
import { E911AddressesTable } from './devices/e911-addresses-table';

const E911ViewTab = {
  ADDRESS_VIEW: 'address-view',
  DEVICE_VIEW: 'device-view',
} as const;

type E911ViewTabType = (typeof E911ViewTab)[keyof typeof E911ViewTab];

type DeviceViewRow = {
  deviceId: string;
  deviceName: string;
  macAddress: string;
  location: string;
  e911AddressId: string;
  e911AddressName: string;
  e911Address: string;
  addressAssignment: 'assigned' | 'not-assigned';
  connectionStatus: 'active' | 'inactive';
};

type DeviceListItem = NonNullable<Awaited<ReturnType<typeof DevicesApi.ListDevices>>['devices']>[number];

const ACTIVE_DEVICE_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;

const AddressAssignment = {
  ASSIGNED: 'assigned',
  NOT_ASSIGNED: 'not-assigned',
} as const;

const ConnectionStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

const getAddressAssignmentOptions = (t: ReturnType<typeof useTranslation<'phone'>>['t']) => [
  { value: AddressAssignment.ASSIGNED, label: t('Assigned') },
  { value: AddressAssignment.NOT_ASSIGNED, label: t('Not Assigned') },
];

const getConnectionStatusOptions = (t: ReturnType<typeof useTranslation<'phone'>>['t']) => [
  { value: ConnectionStatus.ACTIVE, label: t('Active') },
  { value: ConnectionStatus.INACTIVE, label: t('Inactive') },
];

export const E911AddressIndex = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const [isE911EnforcementEnabled] = PhoneConfigOrg.useWetag('e911-enforcement');
  const addressModalControl = useModalControl();
  const complianceModalControl = useModalControl();
  const assignE911AddressModalControl = useModalControl();

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { navigate } = useSettingsNavigate();
  const search = useSettingsSearch<{ Search: { tab?: string; openAssignModal?: string } }>();
  const [assignDeviceId, setAssignDeviceId] = useState<string | undefined>(undefined);
  const [assignDeviceName, setAssignDeviceName] = useState<string | undefined>(undefined);
  const [assignDeviceMacAddress, setAssignDeviceMacAddress] = useState<string | undefined>(undefined);
  const [assignDeviceE911AddressId, setAssignDeviceE911AddressId] = useState<string | undefined>(undefined);
  const [assignModalInstanceKey, setAssignModalInstanceKey] = useState(0);
  const hasHandledRouteAssignOpenRef = useRef(false);
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const locations =
    settingsTenantLocation?.children && settingsTenantLocation.children.length > 0
      ? settingsTenantLocation?.children
      : [settingsTenantLocation].filter(Boolean);

  const locationIds = locations.map((loc: { id: string }) => loc.id);
  const locationId = locationIds[0];
  const isMultiLocation = settingsTenantLocation?.locationType === 'unify';

  const { data: { addresses } = { addresses: [] } } = useQuery({
    queryKey: [tenantId, ...E911QueryKeys.queryKeys.listAddresses(), { includes: { e911PhoneNumber: true } }],
    queryFn: () => E911Api.list({ tenantId, includes: { e911PhoneNumber: true } }),
    select: (data) => ({
      ...data,
      addresses: data.addresses
        ?.slice()
        .sort((firstAddress, secondAddress) => firstAddress.name.localeCompare(secondAddress.name)),
    }),
    enabled: !!tenantId,
    meta: {
      error: {
        message: 'E911AddressIndex:useQuery | Failed to fetch E911 addresses',
        data: { tenantId },
      },
    },
  });

  const { data: { devices = [] } = { devices: [] }, isLoading: isLoadingDevices } = useQuery({
    queryKey: [tenantId, ...DevicesQueryKey.queryKeys.devicesList(), locationIds],
    queryFn: () =>
      DevicesApi.ListDevices({
        tenantId,
        locationIds,
        include: [DeviceInclude_Enum.E911_ADDRESS, DeviceInclude_Enum.REGISTRATION],
      }),
    enabled: !!tenantId,
    staleTime: 0,
    select: (data) => ({
      ...data,
      devices: data.devices
        ?.slice()
        .sort((firstDevice, secondDevice) => firstDevice.name.localeCompare(secondDevice.name)),
    }),
    meta: {
      error: {
        message: 'E911AddressIndex:useQuery | Failed to fetch devices list for E911 device view',
        data: { tenantId, locationIds },
      },
    },
  });

  const { data: unassignedE911Devices = [] } = useQuery({
    queryKey: queryKeys.settings.listUnassignedE911Devices(tenantId),
    queryFn: () => DevicesApi.ListUnassignedE911Devices({ tenantId }),
    enabled: !!tenantId && isE911EnforcementEnabled,
    staleTime: 0,
    select: (data) =>
      data?.unassignedDevices
        ?.slice()
        .sort((firstDevice, secondDevice) => firstDevice.name.localeCompare(secondDevice.name)) ?? [],
    meta: {
      error: {
        message: 'AssignE911AddressModal:useQuery | Failed to fetch unassigned E911 devices',
        data: { tenantId },
      },
    },
  });

  const now = Date.now();
  const unassignedActiveDevices = unassignedE911Devices.filter((device) => {
    const lastRegisteredAt = Date.parse(device.lastRegisteredAt ?? '');
    return Number.isFinite(lastRegisteredAt) && now - lastRegisteredAt < ACTIVE_DEVICE_WINDOW_MS;
  });

  const unassignedDeviceIds = new Set(unassignedE911Devices.map((device) => device.deviceId));
  const unassignedActiveDeviceIds = new Set(unassignedActiveDevices.map((device) => device.deviceId));

  const unassignedDeviceCount = unassignedActiveDevices.length;
  const showE911Banner = isE911EnforcementEnabled && unassignedDeviceCount > 0;

  const [selectedTab, setSelectedTab] = useState<E911ViewTabType>(
    search?.tab === E911ViewTab.ADDRESS_VIEW ? E911ViewTab.ADDRESS_VIEW : E911ViewTab.DEVICE_VIEW
  );
  const effectiveSelectedTab = isE911EnforcementEnabled ? selectedTab : E911ViewTab.ADDRESS_VIEW;
  const [addressAssignmentSelections, setAddressAssignmentSelections] = useState<string[]>([]);
  const [connectionStatusSelections, setConnectionStatusSelections] = useState<string[]>([]);

  const addressAssignmentFilterField = useFormField({ type: 'checklist', value: addressAssignmentSelections }, [
    JSON.stringify(addressAssignmentSelections),
  ]);

  const connectionStatusFilterField = useFormField({ type: 'checklist', value: connectionStatusSelections }, [
    JSON.stringify(connectionStatusSelections),
  ]);

  const deviceRows: DeviceViewRow[] = useMemo(
    () =>
      devices
        .filter((device) => device.type !== DeviceType_Enum.MOBILE_APP)
        .map((device: DeviceListItem) => {
          const isUnassigned = unassignedDeviceIds.has(device.deviceId);
          const isActiveUnassigned = unassignedActiveDeviceIds.has(device.deviceId);
          const isAssigned = !isUnassigned;
          const latestConnectedAt = Math.max(
            ...(device.sipProfiles?.map((sipProfile) => Date.parse(sipProfile.registration?.lastConnected ?? '')) ?? [
              Number.NaN,
            ])
          );
          const isActiveAssigned =
            Number.isFinite(latestConnectedAt) && now - latestConnectedAt < ACTIVE_DEVICE_WINDOW_MS;

          const matchedAddress = addresses?.find((addr) => addr.id === device.e911Address?.id);
          const e911Address = matchedAddress ? formatE911AddressForDisplay(matchedAddress) : '';

          return {
            deviceId: device.deviceId,
            deviceName: device.name,
            macAddress: device.macAddress ?? '',
            location: device.location?.name ?? '',
            e911AddressId: matchedAddress?.id ?? '',
            e911AddressName: matchedAddress?.name ?? '',
            e911Address,
            addressAssignment: matchedAddress?.id ? AddressAssignment.ASSIGNED : AddressAssignment.NOT_ASSIGNED,
            connectionStatus:
              (isAssigned && isActiveAssigned) || (!isAssigned && isActiveUnassigned)
                ? ConnectionStatus.ACTIVE
                : ConnectionStatus.INACTIVE,
          };
        }),
    [devices, unassignedDeviceIds, unassignedActiveDeviceIds, addresses, t]
  );

  const addressAssignmentOptions = useMemo(() => getAddressAssignmentOptions(t), [t]);

  const connectionStatusOptions = useMemo(() => getConnectionStatusOptions(t), [t]);

  const filteredDeviceRows = useMemo(() => {
    if (addressAssignmentSelections.length === 0 && connectionStatusSelections.length === 0) {
      return deviceRows;
    }

    return deviceRows.filter((row) => {
      const matchesAddressAssignment =
        addressAssignmentSelections.length === 0 || addressAssignmentSelections.includes(row.addressAssignment);
      const matchesConnectionStatus =
        connectionStatusSelections.length === 0 || connectionStatusSelections.includes(row.connectionStatus);

      return matchesAddressAssignment && matchesConnectionStatus;
    });
  }, [deviceRows, addressAssignmentSelections, connectionStatusSelections]);

  useEffect(() => {
    if (search?.tab === E911ViewTab.DEVICE_VIEW) {
      setSelectedTab(E911ViewTab.DEVICE_VIEW);
    }
  }, [search?.tab]);

  useEffect(() => {
    if (search?.openAssignModal !== 'true') {
      hasHandledRouteAssignOpenRef.current = false;
      return;
    }

    if (hasHandledRouteAssignOpenRef.current) {
      return;
    }

    hasHandledRouteAssignOpenRef.current = true;
    setSelectedTab(E911ViewTab.DEVICE_VIEW);
    setAssignModalInstanceKey((prev) => prev + 1);
    assignE911AddressModalControl.openModal();

    navigate({ to: '/phone/e911', search: { tab: E911ViewTab.DEVICE_VIEW } });
  }, [assignE911AddressModalControl, navigate, search?.openAssignModal]);

  const handleAssignAddressesFromBanner = () => {
    setSelectedTab(E911ViewTab.DEVICE_VIEW);
    setAssignModalInstanceKey((prev) => prev + 1);
    assignE911AddressModalControl.openModal();
  };

  return (
    <PhoneSettingsPage
      title={isE911EnforcementEnabled ? t('Emergency Calling (E911)') : t('E911 Addresses')}
      subtitle={
        <>
          {isE911EnforcementEnabled ? (
            <Trans>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--fabric-space-gap-md)',
                }}
              >
                <div css={{ display: 'flex', flexDirection: 'column', gap: 'var(--fabric-space-gap-md)' }}>
                  <Text>
                    Ensure that your team gets fast and effective assistance from 911 dispatchers during emergencies.
                    Assign E911 addresses to devices in the <strong>Device Assignments</strong> tab. Manage your E911
                    Addresses in the <strong>Address Management</strong> tab.
                  </Text>
                  <Text>
                    Learn more about E911 compliance with Weave phones {''}
                    <TextLink onClick={complianceModalControl.openModal}>here</TextLink>.
                  </Text>
                </div>
              </div>
            </Trans>
          ) : (
            <Trans>
              Edit an E911 address or add a new one. Assign an E911 address to a device in the device settings when you
              edit the device. Learn more about E911 compliance with Weave phones{' '}
              <TextLink onClick={complianceModalControl.openModal}>here</TextLink>.
            </Trans>
          )}

          {!addresses?.length && (
            <BannerNotification
              css={{ marginTop: 'var(--fabric-space-gap-md)' }}
              message={
                <Text size='medium'>
                  <Trans>
                    You do not have an E911 address. Add an address and assign it to your devices in{' '}
                    <TextLink onClick={() => navigate({ to: '/phone/devices' })}>Device Settings</TextLink>.
                  </Trans>
                </Text>
              }
              status='warn'
            />
          )}
          {showE911Banner && (
            <div css={{ marginTop: 'var(--fabric-space-gap-lg)' }}>
              <InlineNotification status='error'>
                <div>
                  <Text size='medium'>
                    {t('{{count}} active device is missing an E911 address.', {
                      count: unassignedActiveDevices.length,
                    })}
                  </Text>
                  <TextLink
                    size='medium'
                    onClick={handleAssignAddressesFromBanner}
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'e911',
                      details: 'assign-e911-addresses-banner',
                    })}
                  >
                    {unassignedActiveDevices.length === 1 ? t('Assign Address') : t('Assign Addresses')}
                  </TextLink>
                </div>
              </InlineNotification>
            </div>
          )}
        </>
      }
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: isE911EnforcementEnabled ? t('Emergency Calling (E911)') : t('E911 Addresses'), to: '/phone/e911' },
      ]}
    >
      {isE911EnforcementEnabled && (
        <Tabs initialTab={selectedTab} onChange={(tabId) => setSelectedTab(tabId as E911ViewTabType)}>
          <Tabs.Bar css={{ maxWidth: 'fit-content', marginBottom: 'var(--fabric-space-gap-lg)' }}>
            <Tabs.Tab id={E911ViewTab.DEVICE_VIEW}>{t('Device Assignments')}</Tabs.Tab>
            <Tabs.Tab id={E911ViewTab.ADDRESS_VIEW}>{t('Address Management')}</Tabs.Tab>
          </Tabs.Bar>
        </Tabs>
      )}

      {effectiveSelectedTab === E911ViewTab.ADDRESS_VIEW && (
        <E911AddressesTable
          tenantId={tenantId}
          addresses={addresses}
          locationId={locationId}
          addressModalControl={addressModalControl}
        />
      )}

      {effectiveSelectedTab === E911ViewTab.DEVICE_VIEW && (
        <Table
          isLoading={isLoadingDevices}
          hasGlobalSearch
          globalSearchConfig={{
            position: 'right',
          }}
          customToolbarRender={() => (
            <div css={deviceViewFiltersRowStyle}>
              <DropdownFilterChipField
                {...addressAssignmentFilterField}
                name='addressAssignment'
                label={t('Address Assignment')}
                filterIsActive={addressAssignmentSelections.length > 0}
                showClearAll={false}
                onApply={({ value, close }) => {
                  setAddressAssignmentSelections(value as string[]);
                  close();
                }}
              >
                {addressAssignmentOptions.map((option) => (
                  <DropdownFilterChipField.Option key={option.value} value={option.value}>
                    {option.label}
                  </DropdownFilterChipField.Option>
                ))}
              </DropdownFilterChipField>

              <DropdownFilterChipField
                {...connectionStatusFilterField}
                name='connectionStatus'
                label={t('Connection Status')}
                filterIsActive={connectionStatusSelections.length > 0}
                showClearAll={false}
                onApply={({ value, close }) => {
                  setConnectionStatusSelections(value as string[]);
                  close();
                }}
              >
                {connectionStatusOptions.map((option) => (
                  <DropdownFilterChipField.Option key={option.value} value={option.value}>
                    {option.label}
                  </DropdownFilterChipField.Option>
                ))}
              </DropdownFilterChipField>

              {(addressAssignmentSelections.length > 0 || connectionStatusSelections.length > 0) && (
                <Button
                  variant='tertiary'
                  onClick={() => {
                    setAddressAssignmentSelections([]);
                    setConnectionStatusSelections([]);
                  }}
                >
                  {t('Clear All')}
                </Button>
              )}
            </div>
          )}
          wrapperStyle={deviceViewTableToolbarStyle}
          data={filteredDeviceRows}
          colConfig={[
            {
              Header: t('Device Name'),
              accessor: ({ deviceName }: DeviceViewRow) => deviceName,
              id: 'device-name',
              cellRenderer: (deviceName: string, device?: DeviceViewRow) => (
                <Truncated
                  as={TextLink}
                  textAlign='left'
                  css={{ width: 'fit-content' }}
                  onClick={() => {
                    if (device) {
                      navigate({
                        to: '/phone/devices/:id',
                        params: { id: device.deviceId },
                      });
                    }
                  }}
                >
                  {deviceName}
                </Truncated>
              ),
              sortType: 'string',
              width: 204,
            },
            ...(isMultiLocation
              ? [
                  {
                    Header: t('Location'),
                    accessor: ({ location }: DeviceViewRow) => location,
                    id: 'location',
                    maxWidth: 200,
                    cellRenderer: (location: string) =>
                      location ? <Chip.SingleChip>{location}</Chip.SingleChip> : '--',
                  },
                ]
              : []),
            {
              Header: t('Address Assignment'),
              accessor: ({ addressAssignment }: DeviceViewRow) => addressAssignment,
              id: 'address-assignment',
              cellRenderer: (value: DeviceViewRow['addressAssignment']) => (
                <div css={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
                  <Icon name={value === AddressAssignment.NOT_ASSIGNED ? 'error-filled' : 'success-filled'} />
                  {value === AddressAssignment.NOT_ASSIGNED ? t('Not Assigned') : t('Assigned')}
                </div>
              ),
            },
            {
              Header: t('E911 Address'),
              accessor: ({ e911AddressName, e911Address }: DeviceViewRow) =>
                e911AddressName && e911Address ? `${e911AddressName} (${e911Address})` : e911Address,
              id: 'e911-address',
              disableSortBy: true,
              cellRenderer: (_value: string, row?: DeviceViewRow) => {
                if (!row?.e911Address) {
                  return t('None');
                }

                return row.e911AddressName ? `${row.e911AddressName} (${row.e911Address})` : row.e911Address;
              },
            },
            {
              Header: t('Connection Status'),
              accessor: ({ connectionStatus }: DeviceViewRow) => connectionStatus,
              id: 'connection-status',
              cellRenderer: (connectionStatus: DeviceViewRow['connectionStatus']) => (
                <ConnectionStatusCell connectionStatus={connectionStatus} />
              ),
            },
            {
              Header: () => null,
              accessor: ({ deviceId, deviceName, macAddress, e911AddressId }: DeviceViewRow) => ({
                deviceId,
                deviceName,
                macAddress,
                e911AddressId,
              }),
              id: 'action',
              minWidth: 80,
              width: 80,
              maxWidth: 80,
              disableSortBy: true,
              cellRenderer: ({
                deviceId,
                deviceName,
                macAddress,
                e911AddressId,
              }: {
                deviceId: string;
                deviceName: string;
                macAddress: string;
                e911AddressId: string;
              }) => (
                <TextLink
                  size='medium'
                  onClick={() => {
                    setAssignDeviceId(deviceId);
                    setAssignDeviceName(deviceName);
                    setAssignDeviceMacAddress(macAddress);
                    setAssignDeviceE911AddressId(e911AddressId || undefined);
                    setAssignModalInstanceKey((prev) => prev + 1);
                    assignE911AddressModalControl.openModal();
                  }}
                >
                  {t('Assign')}
                </TextLink>
              ),
            },
          ]}
          emptyStateConfig={{
            type: 'sync_your_phone',
            header: t('No Devices Yet'),
            description: t('Device assignment data will appear here soon.'),
          }}
        />
      )}

      <ComplianceModal {...complianceModalControl.modalProps} />
      <AssignE911AddressModal
        key={assignModalInstanceKey}
        {...assignE911AddressModalControl.modalProps}
        onClose={() => {
          setAssignDeviceId(undefined);
          setAssignDeviceName(undefined);
          setAssignDeviceMacAddress(undefined);
          setAssignDeviceE911AddressId(undefined);
          assignE911AddressModalControl.modalProps.onClose();
        }}
        deviceId={assignDeviceId}
        deviceName={assignDeviceName}
        deviceMacAddress={assignDeviceMacAddress}
        assignedAddressId={assignDeviceE911AddressId}
        unassignedActiveDevices={assignDeviceId ? [] : unassignedActiveDevices}
      />
    </PhoneSettingsPage>
  );
};

const ConnectionStatusCell = ({ connectionStatus }: { connectionStatus: DeviceViewRow['connectionStatus'] }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const isActive = connectionStatus === ConnectionStatus.ACTIVE;
  const label = isActive ? t('Active') : t('Inactive');

  return (
    <>
      <div {...triggerProps}>{label}</div>
      <Tooltip {...tooltipProps}>
        {isActive
          ? t('Device has connected (registered) to Weave in the last 5 days')
          : t('Device has not connected (registered) to Weave in the last 5 days')}
      </Tooltip>
    </>
  );
};

export default E911AddressIndex;

const ComplianceModal = ({ onClose, show }: ModalControlModalProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });

  return (
    <Modal minWidth={700} onClose={onClose} show={show}>
      <Modal.Header>{t('E911 Setup Terms and Conditions')}</Modal.Header>
      <Modal.Body>
        <div css={spacing}>
          <Text>
            {t(
              'We want to make sure that you are aware of important differences in the way 9-1-1 service operates with a VoIP phone when compared with traditional telephone service. Here is what you need to keep in mind:'
            )}
          </Text>
          <ul css={spacing}>
            <li>
              {t(
                'With VoIP phone service, your 9-1-1 call is forwarded to a third-party service provider that will automatically or manually route your call to the emergency response center.'
              )}
            </li>
            <li>
              {t(
                'Because you can move your VoIP phone between locations and because, you must immediately inform the emergency operator of your location and contact particulars any time you call 9-1-1.'
              )}
            </li>
            <li>
              {t(
                'Weave will attempt to provide the emergency operator with your service address, so please ensure that your information on file with us is always accurate and updated. If you are unable to provide your location to the emergency operator, emergency services will be dispatched to the address you have provided and may be misdirected to the wrong location.'
              )}
            </li>
            <li>
              {t(
                'VoIP phone service depends not only on your continued subscription for the service, but also on Internet connectivity and power to function. In the event of power, network, or Internet outages, or if your service is disconnected or suspended, you may experience a failure, disruption, or delay in your 9-1-1 service. Do not disconnect your 9-1-1 call until you are told to do so by the emergency operator.'
              )}
            </li>
            <li>
              {t(
                'You must notify members of staff and other potential users of your VoIP phone service of the nature and limitations of 9-1-1 emergency calls. Weave has provided stickers that can be placed on your phones for this purpose.'
              )}
            </li>
            <li>
              {t(
                'If you are not comfortable with the limitations of 9-1-1 emergency calls, we recommend that you terminate the VoIP services or consider an alternate means for accessing traditional 9-1-1 emergency services, such as a cellular telephone.'
              )}
            </li>
          </ul>
          <Text>
            <Trans>
              To review the 9-1-1 limitations, including 9-1-1 disclaimers and limitations of liability, and learn more
              about E911 service, visit{' '}
              <TextLink target='_blank' href='https://www.getweave.com/legal/e911/'>
                https://www.getweave.com/legal/e911/
              </TextLink>
              .
            </Trans>
          </Text>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const spacing = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-md)',
});

const deviceViewFiltersRowStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--fabric-space-gap-md)',
});

const deviceViewTableToolbarStyle = css({
  '.table-toolbar': {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--fabric-space-gap-md)',
    flexWrap: 'wrap',
    width: '100%',
  },
});
